/**
 * CONTRATO DE SAÍDA — de onde vem e o que o quebra
 *
 * A rota `/chat` devolve TEXTO CRU, sem HTML. A forma é fixada em
 * `biodiversity-rag/app/utils/retriever.py` e pelo prompt `atcheza.md` v0.6.13,
 * na ordem: **prosa → bloco de fontes → rodapé do Art. 50**.
 *
 * Essa ordem é produzida por três post-processings encadeados em
 * `retriever_chat`, e `_ensure_disclosure` roda POR ÚLTIMO — de propósito, pra o
 * rodapé cair depois do bloco `[fonte:]`. Ele normaliza em vez de testar
 * presença: remove o rodapé onde estiver e reanexa exatamente um no fim. Logo,
 * em TODO caminho de resposta (LLM, hard gate de zero chunks, falha de áudio,
 * exceção), o rodapé é o último bloco.
 *
 * MUDAR A ORDEM LÁ QUEBRA AQUI. Foi o que aconteceu: até 26/08 o rodapé vinha
 * antes do bloco de fontes, esta função casava a citação ancorada no fim da
 * string (`...$`), e quando o rodapé passou pra depois a regex parou de casar —
 * a função caía no ramo legado, devolvia a resposta inteira como corpo, e os
 * três sintomas saíam juntos: citação na prosa, caixa de referência sumida,
 * rodapé com underscore à vista. Quem mexer no `retriever.py` olhe este arquivo.
 *
 * O rodapé é reconhecido pela FORMA, nunca pelo texto: último bloco, uma linha
 * só, inteiro entre `_..._`. O texto (`_DISCLOSURE`) é fonte de verdade única no
 * backend e não tem cópia aqui — constante duplicada em dois repos que precisam
 * mudar juntos é o acoplamento que causou este defeito. Forma é o que o frontend
 * pode conhecer sem saber o texto, e vale em qualquer idioma.
 *
 * O frontend nunca esconde, traduz, corta nem INVENTA o rodapé: sem rodapé
 * reconhecido, o texto inteiro fica no corpo. Defeito visível vale mais que
 * defeito mascarado — fabricar uma declaração legal no idioma ou na versão
 * errada é pior que não ter nenhuma.
 */

// Marcador de citação, nos idiomas que o LLM usa quando espelha o idioma da
// pergunta. Mesma lista do `_CITATION_PATTERN` do backend (retriever.py:199).
const MARKER = "(?:fontes?|sources?|fuentes?|quellen?|chanzo|vyanzo)";

// Citação no fim do que sobrou depois de tirar o rodapé — o caso do contrato.
// `[\s.,;:!?]*$` tolera a pontuação que o LLM às vezes põe depois do `]`.
const CITATION_AT_END = new RegExp(
  `\\s*\\[${MARKER}:\\s*([^\\]]+)\\][\\s.,;:!?]*$`,
  "i"
);

// Mesma citação em qualquer posição. Rede de segurança pro caso do LLM deixar
// prosa DEPOIS do bloco de fontes: sem isto, a função cairia no ramo legado e
// devolveria tudo como corpo, que é exatamente o defeito de 26/08.
const CITATION_ANYWHERE = new RegExp(`\\[${MARKER}:\\s*([^\\]]+)\\]`, "gi");

// Rodapé: uma linha, abre e fecha com `_`, com conteúdo entre eles.
// `[^\n]+` não atravessa quebra de linha, então um bloco multi-linha entre
// underscores é prosa do LLM e não entra no slot da obrigação legal.
const DISCLOSURE_BLOCK = /^_([^\n]+)_$/;

// Linha em branco separando blocos, tolerando espaço/tab na linha vazia.
const BLANK_LINE = /\n[ \t]*\n/g;

/**
 * Destaca o rodapé do Art. 50 do fim da resposta.
 *
 * @param {string} answer
 * @returns {{body: string, disclosure: string}} `disclosure` sem os underscores
 *  (nem o render nem a cópia os querem, então são cortados uma vez, aqui).
 *  Sem rodapé reconhecido, `body` é a resposta intacta.
 */
function splitDisclosure(answer) {
  const trimmed = answer.trimEnd();
  const separators = [...trimmed.matchAll(BLANK_LINE)];
  const lastSeparator = separators[separators.length - 1];

  const lastBlock = lastSeparator
    ? trimmed.slice(lastSeparator.index + lastSeparator[0].length)
    : trimmed;

  const match = lastBlock.trim().match(DISCLOSURE_BLOCK);
  if (!match) return { body: answer, disclosure: "" };

  return {
    // Sem separador, a resposta é só o rodapé — corpo vazio. É o que
    // `_ensure_disclosure` devolve quando o corpo fica vazio (retriever.py:306).
    body: lastSeparator ? trimmed.slice(0, lastSeparator.index) : "",
    disclosure: match[1].trim(),
  };
}

/**
 * Destaca o bloco de citação do corpo.
 *
 * Havendo mais de um bloco — o `_force_citation_newline` do backend roda com
 * `count=0` justamente porque o LLM às vezes emite dois —, vale a ÚLTIMA
 * ocorrência, e as anteriores ficam visíveis no corpo. Preferimos citação
 * repetida à vista a prosa engolida em silêncio.
 *
 * @param {string} body
 * @returns {{text: string, citation: string, source: string}|null} `citation` é
 *  o bloco CRU, como o backend mandou. A cópia precisa dele: reconstruir
 *  `[fonte: ...]` a partir de `source` exigiria o frontend saber a palavra do
 *  marcador em cada idioma, que é o acoplamento que este arquivo evita.
 */
function splitCitation(body) {
  const atEnd = body.match(CITATION_AT_END);
  if (atEnd) {
    return {
      text: body.slice(0, atEnd.index).trim(),
      citation: atEnd[0].trim(),
      source: atEnd[1].trim(),
    };
  }

  const all = [...body.matchAll(CITATION_ANYWHERE)];
  if (all.length === 0) return null;

  const last = all[all.length - 1];
  const before = body.slice(0, last.index);
  const after = body.slice(last.index + last[0].length);

  return {
    text: `${before.trim()}\n\n${after.trim()}`.trim(),
    citation: last[0].trim(),
    source: last[1].trim(),
  };
}

/**
 * Separa uma resposta crua do backend em corpo, citação e rodapé.
 *
 * Ordem das operações: rodapé PRIMEIRO, citação no que sobrou, legado por
 * último. Tirar o rodapé antes de tudo é o que faz um só caminho servir aos
 * cinco formatos do contrato, inclusive o legado, sem uma regra por formato.
 *
 * @param {string} answer - A resposta crua do backend.
 * @returns {Object} Partes separadas (`{}` se `answer` for vazio):
 *  - `text` {string}: o corpo, com as quebras de linha do backend preservadas.
 *  - `source` {string}: a fonte, sem o invólucro `[fonte: ]`.
 *  - `author` {string}: o autor (só no formato legado; `""` no atual).
 *  - `citation` {string}: o bloco de citação cru, pra cópia (`""` no legado).
 *  - `disclosure` {string}: o rodapé do Art. 50, sem os underscores.
 */
function getAnswerReferences(answer) {
  if (!answer) return {};

  const { body, disclosure } = splitDisclosure(answer);

  const citation = splitCitation(body);
  if (citation) {
    return {
      text: citation.text,
      source: citation.source,
      author: "",
      citation: citation.citation,
      disclosure,
    };
  }

  // Formato legado (rag-python): "corpo\nDocument: fonte\nAuthor: autor".
  const [text, reference] = body.split("Document:");
  if (!reference) {
    return { text: text.trim(), source: "", author: "", citation: "", disclosure };
  }

  const [source, author] = reference.split("Author:");
  return {
    text: text.trim(),
    source: source?.trim() || "",
    author: author?.trim() || "",
    citation: "",
    disclosure,
  };
}

export { getAnswerReferences };
